import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';

// A connected group of buttons where exactly one option is active (iOS-style
// segmented control). `size="sm"` is the compact variant for dense toolbars.
const StyledDiv = styledComponent('div', {
  display: 'inline-flex',
  alignItems: 'center',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.radii.input,
  overflow: 'hidden',
  background: theme.colors.bgCard,

  '& .st-btn': {
    border: 'none',
    background: 'none',
    padding: '6px 16px',
    fontSize: theme.sizes.bodyFontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.textMuted,
    cursor: 'pointer',
    transition: theme.transitions.default,
    borderLeft: `1px solid ${theme.colors.borderLight}`,
  },
  '&.st-sm .st-btn': {
    padding: '5px 12px',
    fontSize: theme.sizes.smallFontSize,
  },
  '& .st-btn:first-of-type': {
    borderLeft: 'none',
  },
  '& .st-btn:hover': {
    background: theme.colors.bgMuted,
  },
  '& .st-btn:focus-visible': {
    outline: 'none',
    boxShadow: `inset 0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .st-btn.is-active': {
    background: theme.colors.bgMuted,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
  },
});

function SegmentedToggle({ options, value, onChange, ariaLabel, size }) {
  return (
    <StyledDiv
      role="group"
      aria-label={ariaLabel}
      className={size === 'sm' ? 'st-sm' : undefined}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`st-btn${value === opt.value ? ' is-active' : ''}`}
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          title={opt.title}
        >
          {opt.label}
        </button>
      ))}
    </StyledDiv>
  );
}

SegmentedToggle.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      title: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
};

SegmentedToggle.defaultProps = {
  ariaLabel: undefined,
  size: 'md',
};

export default SegmentedToggle;
